import {
  BankOutlined,
  CalendarOutlined,
  DashboardOutlined,
  EditOutlined,
  FileTextOutlined,
  LineChartOutlined,
  MessageOutlined,
  SolutionOutlined,
  TeamOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { Badge } from 'antd';

import { ADMIN_ROLE, CLIENT_ROLE, RECRUITER_ROLE } from '@/constants/roles';
import {
  ANALYTICS,
  CALENDAR,
  CANDIDATES,
  COMPANIES,
  DASHBOARD,
  HIRING_APPROVALS,
  JOBS,
  MASTER_CONTRACTS,
  MESSAGES,
  RECRUITER_CONTRACTS,
  RECRUITER_MANAGEMENT,
  RECRUITERS,
} from '@/constants/routes';
import { hasNavPermission } from '@/utils/permissions';

import type { User } from '@/types/user';
import type { TFunction } from 'i18next';
import type { ReactNode } from 'react';

interface NavItem {
  key: string;
  icon: ReactNode;
  title: string;
}

export function getNavigationItems(
  user: User,
  t: TFunction,
  globalUnread: number,
): NavItem[] {
  const role = user.role;
  const isAdmin = role === ADMIN_ROLE;
  const isClient = role === CLIENT_ROLE;
  const isRecruiter = role === RECRUITER_ROLE;

  const items: NavItem[] = [];

  items.push({
    key: DASHBOARD,
    icon: <DashboardOutlined />,
    title: t('MENU_DASHBOARD_TOOLTIP'),
  });

  items.push({
    key: JOBS,
    icon: <SolutionOutlined />,
    title: t('JOBS'),
  });

  items.push({
    key: CANDIDATES,
    icon: <TeamOutlined />,
    title: t('CANDIDATES'),
  });

  // Calendar is recruiter-only: planned interviews for agency users.
  if (isRecruiter) {
    items.push({
      key: CALENDAR,
      icon: <CalendarOutlined />,
      title: t('MENU_CALENDAR_TOOLTIP'),
    });
  }

  if (user.can_access_invoices !== false) {
    items.push({
      key: HIRING_APPROVALS,
      icon: <FileTextOutlined />,
      title: t('FEE_APPROVALS'),
    });
  }

  if (
    isClient &&
    user.company?.talent_supplier_module_enabled &&
    hasNavPermission(user, 'view_suppliers')
  ) {
    items.push({
      key: RECRUITER_MANAGEMENT,
      icon: <UserOutlined />,
      title: t('RECRUITER_MANAGEMENT'),
    });
  }

  const clientCanSeeAnalytics =
    isClient &&
    user.company?.analytics_module_enabled &&
    hasNavPermission(user, 'view_analytics');
  const recruiterCanSeeAnalytics =
    isRecruiter && Boolean(user.company?.enable_analytics);

  if (clientCanSeeAnalytics || recruiterCanSeeAnalytics) {
    items.push({
      key: ANALYTICS,
      icon: <LineChartOutlined />,
      title: t('MENU_ANALYTICS_TOOLTIP'),
    });
  }

  if (
    isClient &&
    user.company?.company_wide_fee_settings_enabled &&
    hasNavPermission(user, 'view_mastercontracts')
  ) {
    items.push({
      key: MASTER_CONTRACTS,
      icon: <EditOutlined />,
      title: t('MENU_MASTER_CONTRACTS_TOOLTIP'),
    });
  }

  if (isRecruiter) {
    items.push({
      key: RECRUITER_CONTRACTS,
      icon: <EditOutlined />,
      title: t('MENU_CONTRACTS'),
    });
  }

  if (isAdmin) {
    items.push(
      {
        key: RECRUITERS,
        icon: <UserOutlined />,
        title: t('AGENCIES'),
      },
      {
        key: COMPANIES,
        icon: <BankOutlined />,
        title: t('CLIENTS'),
      },
    );
  }

  if (hasNavPermission(user, 'view_chat')) {
    items.push({
      key: MESSAGES,
      icon: (
        <Badge count={globalUnread}>
          <MessageOutlined />
        </Badge>
      ),
      title: t('MENU_MESSAGES_TOOLTIP'),
    });
  }

  return items;
}

/**
 * Longest-pathname-prefix match against the supplied nav keys. Sub-view query
 * parameters (e.g. ?filter=in-process) are intentionally ignored — sub-views
 * are surfaced inside the page itself, not in the sidebar.
 */
export function isSelected(
  items: NavItem[],
  key: string,
  pathname: string,
): boolean {
  const matchingKey = items
    .map((item) => item.key)
    .filter((k) => pathname === k || pathname.startsWith(`${k}/`))
    .sort((a, b) => b.length - a.length)[0];
  return matchingKey === key;
}
